import React, { type MouseEvent, useCallback, useState, useRef } from 'react';
import { cssMap } from '@compiled/react';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import { TransitionComponent } from '@atlassian/jira-common-directory-v2/src/components/transition-component/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';

type Props = {
	isPrivate: boolean;
	isSimple: boolean;
	canEdit: boolean;
	defaultOpen: boolean;
} & {
	name?: string;
	showSpinner: boolean;
	editBoardUrl: string;
	onEdit: (arg1: AnalyticsEvent) => void;
	onDelete: (
		event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
		arg1: AnalyticsEvent,
	) => void;
	onCopy: (
		event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
		arg1: AnalyticsEvent,
	) => void;
	onMove: (arg1: AnalyticsEvent) => void;
};

type State = {
	isOpen: boolean;
};

const DropdownMenuWithAnalytics = ComponentWithAnalytics('dropdownMenu', {
	onOpenChange: 'open',
})(DropdownMenu);

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

export default function Actions(props: Props): React.JSX.Element {
	const {
		name = undefined,
		isPrivate = false,
		isSimple = false,
		canEdit = true,
		defaultOpen = false,
		showSpinner = false,
		editBoardUrl = '',
		onEdit = noop,
		onDelete = noop,
		onCopy = noop,
		onMove = noop,
	} = props;

	const { formatMessage } = useIntl();

	const [isDropdownOpen, setIsDropdownOpen] = useState(defaultOpen);

	const { setReturnFocusTo } = useModalDialogActions();
	const moreActionsButtonRef = useRef<HTMLButtonElement>(null);
	const shouldRegainFocus = useRef(false);

	const onOpenChange = useCallback(({ isOpen }: State) => {
		setIsDropdownOpen(isOpen);
	}, []);

	/**
	 * Close the menu with a minimal delay so the inner handlers are able to react on keypress properly.
	 * In its current implementation if we close the menu right away it won't open the focused `href`.
	 * Ideally we have to get right of this and few more methods when the bug linked below is fixed.
	 * @see https://ecosystem.atlassian.net/browse/AK-4467
	 */
	const delayedClose = useCallback(() => {
		setTimeout(() => setIsDropdownOpen(false));
	}, []);

	const handleEdit = useCallback(
		(e: MouseEvent<Element>, analytics: AnalyticsEvent) => {
			onEdit(analytics);
			delayedClose();
		},
		[delayedClose, onEdit],
	);

	const renderSettingsAction = () => (
		<TransitionComponent href={editBoardUrl} onClick={handleEdit}>
			{(dropdownProps) => (
				/* @ts-expect-error - Type '{ children: string; href: string; onClick: OnClick; }' is not assignable to type 'Omit<{ children: ReactNode; component?: ComponentType<PropsWithChildren<CustomItemComponentProps>> | undefined; ... 14 more ...; key?: Key | ... 1 more ... | undefined; }, keyof WithAnalyticsEventsProps>'. */
				<DropdownItemWithAnalytics {...dropdownProps}>
					{formatMessage(messages.editAction)}
				</DropdownItemWithAnalytics>
			)}
		</TransitionComponent>
	);

	const renderCopyAction = () => {
		const copyWithAnalytics = (
			event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
			arg1: AnalyticsEvent,
		) => {
			onCopy(event, arg1);
			setReturnFocusTo(moreActionsButtonRef);
		};

		return (
			<DropdownItemWithAnalytics onClick={copyWithAnalytics}>
				{formatMessage(messages.copyAction)}
			</DropdownItemWithAnalytics>
		);
	};

	const renderMoveAction = () => (
		<DropdownItemWithAnalytics onClick={onMove}>
			{formatMessage(messages.moveAction)}
		</DropdownItemWithAnalytics>
	);

	const renderDeleteAction = () => {
		const deleteWithAnalytics = (
			event: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
			arg1: AnalyticsEvent,
		) => {
			onDelete(event, arg1);
			setReturnFocusTo(moreActionsButtonRef);
		};

		return (
			<DropdownItemWithAnalytics onClick={deleteWithAnalytics}>
				{formatMessage(messages.deleteAction)}
			</DropdownItemWithAnalytics>
		);
	};

	const renderActions = () => {
		if (isSimple) {
			return null;
		}

		/**
		 * legacy logic: https://stash.atlassian.com/projects/JIRACLOUD/repos/jira/browse/jira-components/jira-plugins/jira-agile/greenhopper/src/main/resources/includes/js/rapid/configuration/ManageBoards.soy
		 * check boards spec for more informations: https://extranet.atlassian.com/pages/viewpage.action?pageId=3540485825
		 * corresponding issue: https://jdog.jira-dev.com/browse/KITKAT-274
		 */
		let moveItem = null;
		let deleteItem = null;
		let copyItem = null;
		let settingsItem = null;

		if (isPrivate) {
			if (canEdit) {
				moveItem = renderMoveAction();
			}
			deleteItem = renderDeleteAction();
		} else {
			if (canEdit) {
				moveItem = renderMoveAction();
				deleteItem = renderDeleteAction();
			}
			copyItem = renderCopyAction();
			settingsItem = renderSettingsAction();
		}

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const triggerFunction = ({ triggerRef, ...buttonProps }: { triggerRef: React.Ref<any> }) => {
			const ref = mergeRefs(moreActionsButtonRef ?? null, triggerRef);

			if (showSpinner) {
				shouldRegainFocus.current = true;
			}

			if (!showSpinner && shouldRegainFocus.current && moreActionsButtonRef.current) {
				setReturnFocusTo(moreActionsButtonRef);
				shouldRegainFocus.current = false;
			}

			return (
				<IconButton
					{...buttonProps}
					appearance="subtle"
					icon={MoreIcon}
					ref={ref}
					label={
						isPrivate
							? formatMessage(messages.tooltipPrivate)
							: formatMessage(messages.tooltipWithName, { name })
					}
					isLoading={showSpinner}
					spacing="compact"
					{...(isVisualRefreshEnabled()
						? {
								isTooltipDisabled: false,
								tooltip: {
									content: formatMessage(messages.moreActionsTooltip),
									hideTooltipOnClick: true,
								},
							}
						: {})}
				/>
			);
		};

		return (
			<DropdownMenuWithAnalytics
				placement="bottom-end"
				isOpen={isDropdownOpen}
				onOpenChange={onOpenChange}
				trigger={triggerFunction}
				shouldRenderToParent
			>
				<DropdownItemGroup>
					{settingsItem}
					{copyItem}
					{moveItem}
					{deleteItem}
				</DropdownItemGroup>
			</DropdownMenuWithAnalytics>
		);
	};

	return (
		<Flex
			justifyContent="end"
			xcss={styles.container}
			testId="boards-main.content.cells.actions.container"
		>
			{renderActions()}
		</Flex>
	);
}

const styles = cssMap({
	container: {
		paddingLeft: token('space.100'),
		paddingBlock: token('space.100'),
		paddingRight: token('space.150'),
	},
});
